import { useEffect, useState } from 'react';

type MonitoringSourceHealth = {
  sourceKey: string;
  displayName: string;
  domain: 'publication' | 'movement' | string;
  role: string;
  enabled: boolean;
  jurisdiction: string;
  coverageState: string;
  status: 'HEALTHY' | 'DEGRADED' | 'FAILED' | 'DISABLED' | 'UNVERIFIED' | string;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  lastResultCount: number;
  lastNewCount: number;
  lastDuplicateCount: number;
};

type MonitoringHealthResponse = {
  success?: boolean;
  coverage?: { state?: string; sources?: MonitoringSourceHealth[] };
  error?: string;
};

const formatDate = (value: string | null) => {
  if (!value) return 'Nunca';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

const statusClass = (status: string) => {
  if (status === 'HEALTHY') return 'bg-emerald-100 text-emerald-800';
  if (status === 'FAILED') return 'bg-red-100 text-red-800';
  if (status === 'DEGRADED') return 'bg-amber-100 text-amber-800';
  if (status === 'DISABLED') return 'bg-slate-100 text-slate-700';
  return 'bg-blue-100 text-blue-800';
};

export default function PublicationMonitoringHealthPanel() {
  const [data, setData] = useState<MonitoringHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/legal-monitoring/health');
      const payload = await response.json() as MonitoringHealthResponse;
      if (!response.ok || payload.success === false) throw new Error(payload.error || 'Não foi possível consultar a saúde das fontes.');
      setData(payload);
      setError('');
    } catch (cause: any) {
      setError(cause?.message || 'Não foi possível consultar a saúde das fontes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const sources = data?.coverage?.sources ?? [];

  return (
    <section className="rounded-2xl border border-background-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground-900">Saúde e cobertura das fontes</h3>
          <p className="text-xs text-foreground-500">Consulta manual: zero resultados continua distinto de falha da fonte.</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading} className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-50 disabled:opacity-50">
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
      {!error && sources.length === 0 && !loading && <p className="mt-3 text-xs text-foreground-500">Nenhuma fonte registrada.</p>}

      {sources.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-background-200 text-foreground-500">
              <tr><th className="px-2 py-2">Fonte</th><th className="px-2 py-2">Capacidade</th><th className="px-2 py-2">Cobertura</th><th className="px-2 py-2">Estado</th><th className="px-2 py-2">Última tentativa</th><th className="px-2 py-2">Resultado</th><th className="px-2 py-2">Último erro</th></tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr key={`${source.sourceKey}:${source.jurisdiction}`} className="border-b border-background-100 last:border-0">
                  <td className="px-2 py-2"><div className="font-medium text-foreground-800">{source.displayName}</div><div className="text-[11px] text-foreground-500">{source.sourceKey}</div></td>
                  <td className="px-2 py-2">{source.domain === 'movement' ? 'Movimentações' : 'Publicações'}</td>
                  <td className="px-2 py-2">{source.jurisdiction} · {source.coverageState}</td>
                  <td className="px-2 py-2"><span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${statusClass(source.status)}`}>{source.status}</span></td>
                  <td className="px-2 py-2">{formatDate(source.lastAttemptAt)}<div className="text-[11px] text-foreground-500">Sucesso: {formatDate(source.lastSuccessAt)}</div></td>
                  <td className="px-2 py-2">{source.lastResultCount} encontrados · {source.lastNewCount} novos · {source.lastDuplicateCount} duplicados</td>
                  <td className="max-w-xs px-2 py-2 text-red-700">{source.lastError || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
